// src/components/AuthForms.js

import React from 'react';

// --- Helper Icon Components for a cleaner look ---

const UserIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
);

const MailIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
);

const LockIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
);

const InviteIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 12a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
);

// --- Main AuthForms Component ---

function AuthForms({
  showLoginForm,
  showRegisterForm,
  loginData,
  registerData,
  handleAuthInputChange,
  handleLoginSubmit,
  handleRegisterSubmit,
  setShowLoginForm,
  setShowRegisterForm,
}) {
  return (
    // Main container with a sleek background
    <div className="auth-container">
      <div className="auth-panel">
        {/* Branding Panel (Left side) */}
        <div className="auth-branding-panel">
          <div className="p-12 text-white">
            <h1 className="text-4xl font-bold mb-4">LegalOps</h1>
            <p className="text-lg text-gray-200">
              Streamlining legal practice management. Welcome to the future of law firm efficiency.
            </p>
            <div className="mt-8 border-l-4 border-accent pl-4">
              <p className="text-gray-300 italic">"The secret of getting ahead is getting started."</p>
              <p className="text-gray-400 text-sm mt-1">- Mark Twain</p>
            </div>
          </div>
        </div>

        {/* Form Panel (Right side) */}
        <div className="auth-form-panel">
          <div className="max-w-md w-full space-y-8 p-10">
            <div>
              <h2 className="mt-6 text-center text-3xl font-extrabold text-primary">
                {showLoginForm ? 'Welcome Back!' : 'Create Your Account'}
              </h2>
              <p className="mt-2 text-center text-sm text-gray-600">
                {showLoginForm ? 'Sign in to continue' : 'Join us today'}
              </p>
            </div>

      {/* Notifications are shown via the global Toast component */}

            {showLoginForm && (
              <form className="mt-8 space-y-6" onSubmit={handleLoginSubmit}>
                <div className="space-y-4">
                  <div className="input-group">
                    <span className="input-icon"><UserIcon /></span>
                    <input
                      id="loginUsername"
                      name="username"
                      type="text"
                      required
                      className="auth-input"
                      placeholder="Username"
                      value={loginData.username}
                      onChange={handleAuthInputChange}
                    />
                  </div>
                  <div className="input-group">
                    <span className="input-icon"><LockIcon /></span>
                    <input
                      id="loginPassword"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      className="auth-input"
                      placeholder="Password"
                      value={loginData.password}
                      onChange={handleAuthInputChange}
                    />
                  </div>
                </div>

                <div>
                  <button type="submit" className="auth-button">
                    Sign In
                  </button>
                </div>
              </form>
            )}

            {showRegisterForm && (
              <form className="mt-8 space-y-6" onSubmit={handleRegisterSubmit}>
                <div className="space-y-4">
                  <div className="input-group">
                    <span className="input-icon"><UserIcon /></span>
                    <input
                      id="registerUsername"
                      name="username"
                      type="text"
                      required
                      className="auth-input"
                      placeholder="Username"
                      value={registerData.username}
                      onChange={handleAuthInputChange}
                    />
                  </div>
                  <div className="input-group">
                    <span className="input-icon"><MailIcon /></span>
                    <input
                      id="registerEmail"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      className="auth-input"
                      placeholder="Email address"
                      value={registerData.email}
                      onChange={handleAuthInputChange}
                    />
                  </div>
                  <div className="input-group">
                    <span className="input-icon"><InviteIcon /></span>
                    <input
                      id="registerInviteCode"
                      name="invite_code"
                      type="text"
                      required
                      className="auth-input"
                      placeholder="Invite Code"
                      value={registerData.invite_code || ''}
                      onChange={handleAuthInputChange}
                    />
                  </div>
                  <div className="input-group">
                    <span className="input-icon"><LockIcon /></span>
                    <input
                      id="registerPassword"
                      name="password"
                      type="password"
                      autoComplete="new-password"
                      required
                      className="auth-input"
                      placeholder="Password"
                      value={registerData.password}
                      onChange={handleAuthInputChange}
                    />
                  </div>
                </div>

                <div>
                  <button type="submit" className="auth-button">
                    Register
                  </button>
                </div>
              </form>
            )}

            <div className="text-sm text-center">
              {showLoginForm ? (
                <p className="text-gray-600">
                  Need to register?{' '}
                  <button className="auth-link" onClick={() => { setShowLoginForm(false); setShowRegisterForm(true); }}>
                    Register with invite code
                  </button>
                </p>
              ) : (
                <p className="text-gray-600">
                  Already have an account?{' '}
                  <button className="auth-link" onClick={() => { setShowRegisterForm(false); setShowLoginForm(true); }}>
                    Login here
                  </button>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthForms;
